import React, { useEffect, useState, useRef } from "react";
import { useLanguage } from "../contexts/LanguageContext";
import LanguageSwitcher from "./LanguageSwitcher";

// spacerBg paints the fixed bar's placeholder. It defaults to the page's white,
// which is invisible against a white first section but reads as a white band
// above one that opens on a tint (stripe-section--gradient starts at #f6f9fc).
// Pages whose first section is tinted pass that section's top colour.
const Navbar = ({ hideButtons = false, hideBanner = false, ctaHref = null, hidePricing = false, logoHref = "/", spacerBg = null }) => {
  const L = useLanguage();
  const [loggedIn, setLoggedIn] = useState(false);
  const [holidayMonth, setHolidayMonth] = useState(0);
  const [holidayEmoji, setHolidayEmoji] = useState('');
  const [holidayColor, setHolidayColor] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // The navbar is fixed, so it no longer occupies space — a spacer of the same
  // height keeps the page from sliding underneath it. Measured rather than
  // hard-coded because the holiday banner rewraps at narrow widths.
  //
  // Measure the collapsed bar only: the mobile menu animates its max-height
  // inside <nav>, so the raw nav height is up to 400px too large mid-transition.
  // Tapping an in-page link closes the menu and jumps in the same tick, so a
  // raw measurement would publish that inflated height as --nav-h and the
  // anchor would land far above its target. Subtracting the menu's own height
  // stays correct at every frame of the animation.
  const navRef = useRef(null);
  const menuRef = useRef(null);
  const [navHeight, setNavHeight] = useState(0);
  useEffect(() => {
    const el = navRef.current;
    if (!el) return;
    const measure = () => {
      const h = el.offsetHeight - (menuRef.current?.offsetHeight || 0);
      setNavHeight(h);
      document.documentElement.style.setProperty("--nav-h", `${h}px`);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    if (menuRef.current) ro.observe(menuRef.current);
    return () => ro.disconnect();
  }, []);




  // Validate the token
  const validateToken = async () => {
    const accessToken = localStorage.getItem("accessToken");

    try {
	  if(!accessToken) return;
      const response = await fetch(`https://api.viralgpt.com/auth/validate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ token: accessToken }),
      });

      if (!response.ok) {
        console.warn("Token is invalid or expired. Redirecting to login...");
        localStorage.removeItem("accessToken");
      } else {
        setLoggedIn(true);
      }
    } catch (err) {
      console.error("Failed to validate token:", err);
      localStorage.removeItem("accessToken");
    }
  };

  // Set holiday theme based on current month
  useEffect(() => {
    const today = new Date();
    const month = today.getMonth() + 1;

    setHolidayMonth(month);

    switch(month) {
      case 1:
        setHolidayEmoji("🎊");
        setHolidayColor("#333333");
        break;
      case 2:
        setHolidayEmoji("💕");
        setHolidayColor("#D81B60");
        break;
      case 3:
        setHolidayEmoji("🍀");
        setHolidayColor("#1B5E20");
        break;
      case 4:
        setHolidayEmoji("🦋");
        setHolidayColor("#2E7D32");
        break;
      case 5:
        setHolidayEmoji("🎉");
        setHolidayColor("#4A148C");
        break;
      case 6:
        setHolidayEmoji("🌴");
        setHolidayColor("#0277BD");
        break;
      case 7:
        setHolidayEmoji("🇺🇸");
        setHolidayColor("#B71C1C");
        break;
      case 8:
        setHolidayEmoji("✏️");
        setHolidayColor("#1A237E");
        break;
      case 9:
        setHolidayEmoji("🌻");
        setHolidayColor("#8B4513");
        break;
      case 10:
        setHolidayEmoji("👻");
        setHolidayColor("#000000");
        break;
      case 11:
        setHolidayEmoji("💫");
        setHolidayColor("#212121");
        break;
      case 12:
        setHolidayEmoji("❄️");
        setHolidayColor("#1B5E20");
        break;
      default:
        break;
    }
  }, []);

  // Check token validity on component mount
  useEffect(() => {
    validateToken();
  }, []);

  return (
    <>
      <style>
        {`
          .holiday-banner {
            background-color: ${holidayColor}66;
            color: white;
            position: relative;
            overflow: hidden;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            backdrop-filter: blur(5px);
          }
          .holiday-banner::before {
            content: "";
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            opacity: 0.08;
            background-image: repeating-linear-gradient(
              45deg,
              transparent,
              transparent 20px,
              currentColor 20px,
              currentColor 40px
            );
            pointer-events: none;
            z-index: 0;
          }
          .holiday-banner a {
            position: relative;
            z-index: 1;
          }
        `}
      </style>

     

      <nav ref={navRef} className="fixed-top" style={{background: '#fff', borderBottom: '1px solid #e3e8ee', boxShadow: '0 2px 4px rgba(50,50,93,0.06)'}}>
		 {/* Holiday Banner — hidden on funnels that show their own offer banner */}
		 <div className="holiday-banner text-center py-2" style={{ display: hideBanner ? 'none' : 'block' }}>
        <a href={ctaHref || "/pricing"} className="text-decoration-none">
          <span className="pill px-4 py-2 rounded-pill d-inline-block" 
            style={{
              fontSize: '0.75rem',
              backgroundColor: holidayColor,
              color: 'white',
              transition: 'all 0.3s ease',
              boxShadow: '0 2px 4px rgba(0,0,0,0.15)'
            }}>
            <strong>
              {holidayEmoji} {holidayMonth ? L.navbar.holidays[holidayMonth] : ""} | {L.navbar.fiftyOffAllPlans} {holidayEmoji}
            </strong>
          </span>
        </a>
      </div>
        <div className="stripe-container px-3 sm:px-6 lg:px-8" style={{ display: hideButtons ? 'none' : 'block' }}>
          <div className="flex justify-between items-center h-16">
            {/* Logo Section */}
            <div className="flex items-center space-x-3">
              <a href={logoHref} className="text-decoration-none">
                <span className="text-2xl flex items-center" style={{fontWeight: 700, letterSpacing: '-0.025em'}}>
                  <img src="/images/logo-icon.png" alt={L.navbar.logoAlt} className="h-8 w-8 mr-2" />
                  <span className="stripe-text-dark">ViralGPT</span>
                </span>
              </a>
            </div>

            {/* Desktop Navigation Links */}
            <div className="hidden md:flex space-x-6 justify-center flex-1">
              {!hidePricing && (
                <a
                  href={ctaHref || "/pricing"}
                  className="stripe-text-body font-semibold flex items-center text-decoration-none"
                  style={{fontSize: '1.05rem', transition: 'color 0.2s ease'}}
                >
                  {L.navbar.pricing}
                </a>
              )}
              <a
                href="#faq"
                className="stripe-text-body font-semibold flex items-center text-decoration-none"
                style={{fontSize: '1.05rem', transition: 'color 0.2s ease'}}
              >
                {L.navbar.faq}
              </a>
              <a
                href="#testimonials"
                className="stripe-text-body font-semibold flex items-center text-decoration-none"
                style={{fontSize: '1.05rem', transition: 'color 0.2s ease'}}
              >
                {L.navbar.reviews}
              </a>
            </div>

            {/* Desktop Right Side Links */}
            <div className="hidden md:flex items-center space-x-4">
              <LanguageSwitcher />
              <a
                href="/login"
                className="stripe-text-body font-medium text-decoration-none"
                style={{fontSize: '0.95rem', transition: 'color 0.2s ease'}}
              >
                {!loggedIn ? L.navbar.logIn : ""}
              </a>
              <a
                href={!loggedIn ? (ctaHref || "/pricing") : "/dashboard"}
                className="stripe-btn stripe-btn--primary text-decoration-none px-4 py-2"
              >
                {!loggedIn ? L.navbar.getStarted : L.navbar.dashboard}
              </a>
            </div>

            {/* Mobile Hamburger Button */}
            <button
              className="md:hidden flex items-center justify-center"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              style={{
                width: '40px', height: '40px', border: 'none', background: 'transparent',
                cursor: 'pointer', padding: 0,
              }}
              aria-label={L.navbar.toggleMenu}
            >
              <div style={{width: '22px', position: 'relative', height: '16px'}}>
                <span style={{
                  display: 'block', position: 'absolute', height: '2px', width: '100%',
                  background: '#0a2540', borderRadius: '2px', transition: 'all 0.25s ease',
                  top: mobileMenuOpen ? '7px' : '0px',
                  transform: mobileMenuOpen ? 'rotate(45deg)' : 'none',
                }}></span>
                <span style={{
                  display: 'block', position: 'absolute', height: '2px', width: '100%',
                  background: '#0a2540', borderRadius: '2px', transition: 'all 0.25s ease',
                  top: '7px', opacity: mobileMenuOpen ? 0 : 1,
                }}></span>
                <span style={{
                  display: 'block', position: 'absolute', height: '2px', width: '100%',
                  background: '#0a2540', borderRadius: '2px', transition: 'all 0.25s ease',
                  top: mobileMenuOpen ? '7px' : '14px',
                  transform: mobileMenuOpen ? 'rotate(-45deg)' : 'none',
                }}></span>
              </div>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div
          ref={menuRef}
          className="md:hidden"
          style={{
            maxHeight: mobileMenuOpen ? '400px' : '0',
            overflow: 'hidden',
            transition: 'max-height 0.3s ease',
            borderTop: mobileMenuOpen ? '1px solid #e3e8ee' : 'none',
            background: '#fff',
          }}
        >
          <div className="px-4 py-4" style={{display: 'flex', flexDirection: 'column', gap: '0.25rem'}}>
            {!hidePricing && (
              <a
                href={ctaHref || "/pricing"}
                className="stripe-text-dark font-semibold text-decoration-none d-block py-2 px-3"
                style={{fontSize: '1.05rem', borderRadius: '10px', transition: 'background 0.2s ease'}}
                onClick={() => setMobileMenuOpen(false)}
                onMouseOver={(e) => e.currentTarget.style.background = '#f6f9fc'}
                onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
              >
                {L.navbar.pricing}
              </a>
            )}
            <a
              href="#faq"
              className="stripe-text-dark font-semibold text-decoration-none d-block py-2 px-3"
              style={{fontSize: '1.05rem', borderRadius: '10px', transition: 'background 0.2s ease'}}
              onClick={() => setMobileMenuOpen(false)}
              onMouseOver={(e) => e.currentTarget.style.background = '#f6f9fc'}
              onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
            >
              {L.navbar.faq}
            </a>
            <a
              href="#testimonials"
              className="stripe-text-dark font-semibold text-decoration-none d-block py-2 px-3"
              style={{fontSize: '1.05rem', borderRadius: '10px', transition: 'background 0.2s ease'}}
              onClick={() => setMobileMenuOpen(false)}
              onMouseOver={(e) => e.currentTarget.style.background = '#f6f9fc'}
              onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
            >
              {L.navbar.reviews}
            </a>
            <div style={{height: '1px', background: 'linear-gradient(90deg, transparent, #e3e8ee, transparent)', margin: '0.5rem 0'}}></div>
            {!loggedIn && (
              <a
                href="/login"
                className="stripe-text-body font-medium text-decoration-none d-block py-2 px-3"
                style={{fontSize: '1rem', borderRadius: '10px', transition: 'background 0.2s ease'}}
                onClick={() => setMobileMenuOpen(false)}
                onMouseOver={(e) => e.currentTarget.style.background = '#f6f9fc'}
                onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
              >
                {L.navbar.logIn}
              </a>
            )}
            <div className="py-2 px-3">
              <LanguageSwitcher />
            </div>
            <a
              href={!loggedIn ? (ctaHref || "/pricing") : "/dashboard"}
              className="stripe-btn stripe-btn--primary text-decoration-none text-center d-block py-3 mt-1"
              onClick={() => setMobileMenuOpen(false)}
            >
              {!loggedIn ? L.navbar.getStarted : L.navbar.dashboard}
            </a>
          </div>
        </div>
      </nav>
      <div style={{ height: navHeight, background: spacerBg || undefined }} aria-hidden="true" />
    </>
  );
};

export default Navbar;
